import { useCallback, useEffect, useRef, useState } from 'react'

import {
  isWorkflowRunStepTerminal,
  isWorkflowRunTerminal,
  WorkflowGateDecision,
  WorkflowRun,
  WorkflowRunStep,
  WorkflowStepOutput,
  WorkflowTemplate,
} from '@digital-twins/shared-types'

import { markSidebarThreadRunning } from '@/components/layout/useSidebarThreads'
import {
  ReviseWorkflowStepPayload,
  UpdateWorkflowRunSegmentPayload,
  SubmitWorkflowGatePayload,
  workflowRunsApi,
  workflowsApi,
} from '@/lib/api'
import {
  shouldPollWorkflowRunReadback,
} from '@/pages/ThreadDetailPage/deriveThreadRunActivityLabel'
import {
  onWorkflowRunStatus,
  onWorkflowRunStep,
  onWorkflowRunSync,
  subscribeToWorkflowRun,
  unsubscribeFromWorkflowRun,
} from '@/lib/services/workflow-socket'
import {
  onQuickStudyRunFrame,
  onQuickStudyRunStatus,
  subscribeToQuickStudyRun,
  unsubscribeFromQuickStudyRun,
} from '@/lib/services/websocket'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'
import { incrementPollWonRepair } from '@/lib/realtime/pollWonMetrics'

/**
 * The ONE way the run surface reads a workflow run (Assistant Platform v1,
 * Step 7.1: the live transport).
 *
 * REST resolves the run, the socket keeps it current, and nothing else on the
 * surface talks to either: every component takes its run through this hook, so
 * no component has to know that `:step` is a hint and `GET /workflow-runs/:token`
 * is the truth.
 *
 * Order matters and is deliberate. `getForThread` lands first and only then does
 * the effect subscribe, which looks like a window where an event could be missed
 * — it is not. The gateway emits `:sync` unconditionally inside its subscribe
 * handler, carrying the run's current state and `isTerminal`, so the join itself
 * closes the gap and a run that finished while nobody was listening still stops
 * the pane spinning.
 *
 * The hook returns the template beside the run on purpose. A run record carries
 * what HAS happened; the gate's spend line has to say what approving WILL do,
 * and that lives in the template's step configs (`panelSize`, `questionsFrom`,
 * fan-out `count`). It is fetched by `run.templateToken`, never `templateSlug`:
 * the route param is `@IsUUID()` and a slug is a 400.
 */

/** One artifact body a reviewer changed at the gate. */
export interface WorkflowGateArtifactEdit {
  /**
   * The PRODUCING step's key (`write_brief`), not the gate's.
   *
   * The server takes gate edits as `Record<stepKey, WorkflowStepOutput>`: a full
   * replacement output per step. A gate flattens artifacts across several steps
   * into one review list, so without this the payload cannot be rebuilt at all.
   */
  stepKey: string
  /** The producing step's `output` slug. */
  slug: string
  /** 0-based fan-out position, or null when the step produced a single artifact. */
  index: number | null
  /** The full edited markdown body, not a diff. */
  body: string
}

/**
 * The gate decision as ONE payload.
 *
 * Every edit made at a gate travels inside `edits`, submitted with the
 * decision, exactly once. There is deliberately no per-artifact save call on
 * this hook: a gate that could persist a field on its own would let a run
 * proceed against artifacts nobody approved.
 */
export interface WorkflowGateSubmission {
  stepId: string
  decision: WorkflowGateDecision
  edits: Array<WorkflowGateArtifactEdit>
  /** Optional reviewer note, stored on the gate's output. */
  note?: string
}

/**
 * One chat-driven revision: the reader's words, plus the cards as they stand.
 *
 * `edits` is the SAME list `WorkflowGateSubmission` carries on Approve, and it
 * is turned into an override by the same builder, because the two have to
 * start from the same bytes: a revision that redid from the stored output
 * would silently throw away every card the reader edited before typing.
 */
export interface WorkflowGateRevision {
  /** The open gate step. The server revises the step in front of it. */
  stepId: string
  /** The reader's message, verbatim. */
  instruction: string
  /** Cards the reader changed by hand, empty when they changed none. */
  edits: Array<WorkflowGateArtifactEdit>
}

/**
 * Why the run surface has nothing to show.
 *
 * `payment_required` is its own state rather than a generic failure because
 * `PaidFirstPaymentGuard` covers every `/workflow-runs` route INCLUDING the
 * reads: an org mid-onboarding gets a 402 on the plain thread lookup, which is
 * a refusal to answer, not a broken page.
 */
export interface WorkflowRunError {
  kind: 'payment_required' | 'request_failed'
  message: string
}

export interface UseWorkflowRunResult {
  run: WorkflowRun | null
  /** The template the run was started from. Null while the run is unknown. */
  template: WorkflowTemplate | null
  isLoading: boolean
  /** Non-null when the run could not be read, or an action was refused. */
  error: WorkflowRunError | null
  submitGate: (submission: WorkflowGateSubmission) => Promise<void>
  /**
   * Ask the writer to redo the step under review. Resolves true once the
   * returned run has been adopted, false when the server refused: the caller
   * shows a busy line until it resolves, and the adopted run is what pulls the
   * gate down while the rewrite runs.
   */
  reviseStep: (revision: WorkflowGateRevision) => Promise<boolean>
  /**
   * Replace who the run's quick study asks while its gate is open (the review
   * panel's "who answers" chips). Resolves true once the returned run was
   * adopted, false when the server refused.
   */
  updateSegment?: (segment: UpdateWorkflowRunSegmentPayload) => Promise<boolean>
  retryStep: (stepId: string) => Promise<void>
  cancelRun: () => Promise<void>
  /**
   * Seed the hook with a run this page just created. `startRun` resolves the
   * full projected run, and without adoption the pane would stay empty until a
   * reload: creating from the thread page is a same-path navigation, so the
   * thread-keyed fetch effect never re-fires. Adopting also lights the sidebar
   * dot through the same path every other run update uses.
   */
  adoptRun: (run: WorkflowRun) => void
}

const PAYMENT_REQUIRED_MESSAGE = 'Workflow runs need an active subscription.'

function readHttpStatus(cause: unknown): number | null {
  if (typeof cause !== 'object' || cause === null) {
    return null
  }
  const response = (cause as { response?: { status?: unknown } }).response
  if (!response || typeof response.status !== 'number') {
    return null
  }
  return response.status
}

function toWorkflowRunError(cause: unknown, fallback: string): WorkflowRunError {
  if (readHttpStatus(cause) === 402) {
    return { kind: 'payment_required', message: PAYMENT_REQUIRED_MESSAGE }
  }
  return { kind: 'request_failed', message: fallback }
}

/** Matches an edit to the stored artifact it replaces, within one step. */
function artifactKey(slug: string, index: number | null): string {
  return `${slug}#${index ?? 0}`
}

/**
 * Whether this gate is allowed to carry overrides at all.
 *
 * Read off the step's own pinned `input.config`, which is the compiled gate
 * config as it stood when the run started (`workflow-run.service.ts` writes
 * `input: { config: step }` onto every step row) and is exactly what the server
 * checks in `readGateArtifactKeys`: `editable !== true` makes the editable key
 * list empty, so ANY `overrides` key is a 400.
 *
 * Deliberately duplicated from `ThreadRunPane/gateArtifacts.ts` rather than
 * imported: `lib/` must not reach into `pages/`, and this is the cheaper of the
 * two wrongs for eight lines that guard the sharpest 400 on the surface.
 */
function gateAllowsOverrides(gateStep: WorkflowRunStep): boolean {
  const input = gateStep.input
  const config = input ? input.config : null
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    // No pinned config means a hand-written row. The gate card derives
    // editability from the template in that case and produces no edits unless
    // it said yes, so an empty edit list is what reaches here.
    return true
  }

  // The same guard `pinnedGateConfig` applies, so the two can never disagree
  // about which gates are editable: a card that let an edit be typed must not
  // meet a payload builder that silently drops it.
  const candidate = config as {
    kind?: unknown
    artifacts?: unknown
    editable?: unknown
  }
  if (candidate.kind !== 'gate' || !Array.isArray(candidate.artifacts)) {
    return true
  }
  return candidate.editable === true
}

/**
 * The gate's edits as the server wants them: a full replacement output per
 * producing step.
 *
 * Every artifact is SPREAD from the one already stored on the run, never
 * rebuilt. `isValidArtifact` accepts `index === undefined || typeof index ===
 * 'number'`, so a reconstructed single-artifact step carrying `index: null`
 * (which is what the client's own entry type holds) is a guaranteed 400.
 * Spreading preserves the absent key, and carries `title` — required, and a
 * field a rebuild is free to forget.
 *
 * Returns null when there is nothing to send, so the caller omits the
 * `overrides` KEY entirely rather than sending `{}`.
 */
function buildGateOverrides(
  run: WorkflowRun,
  submission: WorkflowGateSubmission,
): Record<string, WorkflowStepOutput> | null {
  if (submission.edits.length === 0) {
    return null
  }

  const gateStep = run.steps.find((step) => step.stepId === submission.stepId)
  if (!gateStep || !gateAllowsOverrides(gateStep)) {
    return null
  }

  const bodiesByStepKey = new Map<string, Map<string, string>>()
  for (const edit of submission.edits) {
    const bodies = bodiesByStepKey.get(edit.stepKey) ?? new Map<string, string>()
    bodies.set(artifactKey(edit.slug, edit.index), edit.body)
    bodiesByStepKey.set(edit.stepKey, bodies)
  }

  const overrides: Record<string, WorkflowStepOutput> = {}
  for (const [stepKey, bodies] of bodiesByStepKey) {
    const source = run.steps.find((step) => step.stepKey === stepKey)
    // `outputOverride ?? output`, the same source the gate card read the bodies
    // from, so a gate reopened after an earlier edit replaces what was approved
    // rather than the model's first draft.
    const stored = source ? source.outputOverride ?? source.output : null
    if (!stored || stored.kind !== 'artifacts') {
      continue
    }

    overrides[stepKey] = {
      ...stored,
      artifacts: stored.artifacts.map((artifact) => {
        const body = bodies.get(artifactKey(artifact.slug, artifact.index ?? null))
        if (body === undefined) {
          return { ...artifact }
        }
        return { ...artifact, body }
      }),
    }
  }

  return Object.keys(overrides).length > 0 ? overrides : null
}

/**
 * The single override a revision sends: the step under review, as the reader
 * has it.
 *
 * `POST /revise` takes ONE output, not the map `POST /gate` takes, because the
 * server pins the target to the step in front of the open gate. So the gate's
 * edit list is built into overrides exactly as Approve builds them — same
 * spread, same `outputOverride ?? output` source — and then the one belonging
 * to that target step is picked out. A gate whose edits are all on other steps
 * sends no override, which is the honest answer: nothing the rewrite starts
 * from has changed.
 */
function buildRevisionOverride(
  run: WorkflowRun,
  revision: WorkflowGateRevision,
): WorkflowStepOutput | null {
  const gateStep = run.steps.find((step) => step.stepId === revision.stepId)
  if (!gateStep) {
    return null
  }
  const target = run.steps.find(
    (step) => step.stepIndex === gateStep.stepIndex - 1,
  )
  if (!target) {
    return null
  }

  const overrides = buildGateOverrides(run, {
    stepId: revision.stepId,
    decision: 'approve',
    edits: revision.edits,
  })
  if (!overrides) {
    return null
  }
  return overrides[target.stepKey] ?? null
}

export function useWorkflowRun(threadToken: string | null): UseWorkflowRunResult {
  const [run, setRun] = useState<WorkflowRun | null>(null)
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(threadToken !== null)
  const [error, setError] = useState<WorkflowRunError | null>(null)

  // The actions read the run they are acting on rather than closing over it, so
  // their identity survives every socket frame: a gate card that re-mounted on
  // each `:step` would drop a half-typed edit.
  const runRef = useRef<WorkflowRun | null>(null)
  useEffect(() => {
    runRef.current = run
  }, [run])

  /**
   * The sidebar dot. Keyed by THREAD token, which is why every call lives here:
   * this is the one place the thread and the run are both in hand.
   *
   * Never called from an effect cleanup. The store is module-level and read
   * through `useSyncExternalStore` precisely so the dot survives navigating
   * away from the thread.
   */
  const markRunning = useCallback(
    (isRunning: boolean) => {
      if (!threadToken) {
        return
      }
      markSidebarThreadRunning(threadToken, isRunning)
    },
    [threadToken],
  )

  /** Seeds state from a full projected run, which every mutating call returns. */
  const applyRun = useCallback(
    (next: WorkflowRun) => {
      setRun(next)
      setError(null)
      markRunning(!isWorkflowRunTerminal(next.status))
    },
    [markRunning],
  )

  // A different thread is a different run. Kept in an effect rather than a key
  // so navigating between threads does not remount the whole pane.
  useEffect(() => {
    if (!threadToken) {
      setRun(null)
      setTemplate(null)
      setIsLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setRun(null)
    setTemplate(null)
    setIsLoading(true)
    setError(null)

    workflowRunsApi
      .getForThread(threadToken)
      .then((next) => {
        if (cancelled) {
          return
        }
        setRun(next)
        setIsLoading(false)
        markSidebarThreadRunning(
          threadToken,
          next !== null && !isWorkflowRunTerminal(next.status),
        )
      })
      .catch((cause: unknown) => {
        if (cancelled) {
          return
        }
        setIsLoading(false)
        setError(toWorkflowRunError(cause, 'This run could not be loaded.'))
      })

    return () => {
      cancelled = true
    }
  }, [threadToken])

  // Fetched by token, and separately from the run: a template that 404s or is
  // refused leaves the step list intact, with no spend line and a read-only
  // gate, rather than emptying the pane.
  const templateToken = run ? run.templateToken : null
  useEffect(() => {
    if (!templateToken) {
      setTemplate(null)
      return
    }

    let cancelled = false
    workflowsApi
      .getTemplate(templateToken)
      .then((next) => {
        if (!cancelled) {
          setTemplate(next)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplate(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [templateToken])

  const runToken = run ? run.token : null
  useEffect(() => {
    if (!runToken) {
      return
    }

    let cancelled = false

    /**
     * `:step` and `:status` are thin: no `output`, no `outputOverride`, no
     * `error`, no `isRetryable`. Without this readback a gate card renders zero
     * artifacts and Retry never appears, so it is not an optimization.
     */
    function readBack(): void {
      workflowRunsApi
        .get(runToken as string)
        .then((fresh) => {
          if (cancelled) {
            return
          }
          setRun(fresh)
          if (threadToken) {
            markSidebarThreadRunning(
              threadToken,
              !isWorkflowRunTerminal(fresh.status),
            )
          }
        })
        .catch(() => {
          /* The socket keeps the pane current. A failed readback is not a run
             failure and must not blank a run the user is watching. */
        })
    }

    subscribeToWorkflowRun(runToken)

    // A full replace, never a merge: that is what makes a duplicate frame on
    // reconnect idempotent. The frame carries no run-level identity beyond the
    // token, so the fields it does own are written over the run we hold.
    const stopSync = onWorkflowRunSync(runToken, (payload) => {
      setRun((current) => {
        if (!current || current.token !== payload.runToken) {
          return current
        }
        return {
          ...current,
          status: payload.status,
          currentStepIndex: payload.currentStepIndex,
          steps: payload.steps,
          error: payload.error,
          updatedAt: payload.timestamp,
        }
      })
      if (threadToken) {
        markSidebarThreadRunning(threadToken, !payload.isTerminal)
      }
    })

    const stopStatus = onWorkflowRunStatus(runToken, (payload) => {
      setRun((current) => {
        if (!current || current.token !== payload.runToken) {
          return current
        }
        return {
          ...current,
          status: payload.status,
          currentStepIndex: payload.currentStepIndex,
          updatedAt: payload.timestamp,
        }
      })
      if (threadToken) {
        markSidebarThreadRunning(
          threadToken,
          !isWorkflowRunTerminal(payload.status),
        )
      }
      if (
        isWorkflowRunTerminal(payload.status) ||
        payload.status === 'awaiting_approval'
      ) {
        readBack()
      }
    })

    const stopStep = onWorkflowRunStep(runToken, (payload) => {
      // Merge first. A completed/failed frame is what the thread page watches
      // to refresh living artifacts and outcome lines; do not wait on REST
      // for that cue. readBack still fills output/error for the pane.
      setRun((current) => {
        if (!current || current.token !== payload.runToken) {
          return current
        }
        return {
          ...current,
          steps: current.steps.map((step) =>
            step.stepId === payload.stepId
              ? {
                  ...step,
                  state: payload.state,
                  attempt: payload.attempt,
                  updatedAt: payload.timestamp,
                }
              : step,
          ),
        }
      })
      if (
        isWorkflowRunStepTerminal(payload.state) ||
        payload.state === 'awaiting_approval'
      ) {
        readBack()
      }
    })

    return () => {
      cancelled = true
      stopSync()
      stopStatus()
      stopStep()
      unsubscribeFromWorkflowRun(runToken)
      // The running flag is NOT cleared here. Leaving the thread does not stop
      // the run, and the dot is how the user finds their way back to it.
    }
  }, [runToken, threadToken])

  // Socket frames do not carry QS `{completed, total}`. While twins answer,
  // listen on the linked quick-study run for progress frames; stall repair
  // readback covers anything the stream misses.
  const shouldPollSampling = shouldPollWorkflowRunReadback(run)
  const samplingRunId =
    run?.steps.find(
      (step) =>
        step.stepKind === 'quick_study' &&
        step.state === 'running' &&
        typeof step.externalRef === 'string' &&
        step.externalRef.length > 0,
    )?.externalRef ?? null

  const repairSampling = useCallback(
    (reason: 'reconnect_gap' | 'visibility_gap' | 'stall_timeout') => {
      incrementPollWonRepair('WORKFLOW', reason)
      if (!runToken) {
        return
      }
      workflowRunsApi
        .get(runToken)
        .then((fresh) => applyRun(fresh))
        .catch(() => {
          /* Same as socket readback: a failed repair must not blank the pane. */
        })
    },
    [applyRun, runToken],
  )

  const { notifyActivity: notifySamplingActivity } = useRealtimeRepair({
    active: shouldPollSampling,
    onRepair: repairSampling,
  })

  useEffect(() => {
    if (!samplingRunId || !shouldPollSampling) {
      return
    }

    subscribeToQuickStudyRun(samplingRunId)
    const offFrame = onQuickStudyRunFrame(samplingRunId, (frame) => {
      if (frame.type !== 'progress') {
        return
      }
      const completed =
        typeof frame.completed === 'number' ? frame.completed : null
      const total = typeof frame.total === 'number' ? frame.total : null
      if (completed === null && total === null) {
        return
      }
      notifySamplingActivity()
      setRun((current) => {
        if (!current) {
          return current
        }
        return {
          ...current,
          steps: current.steps.map((step) =>
            step.externalRef === samplingRunId
              ? {
                  ...step,
                  progress: {
                    completed,
                    total,
                  },
                }
              : step,
          ),
        }
      })
    })
    const offStatus = onQuickStudyRunStatus(samplingRunId, () => {
      notifySamplingActivity()
      if (!runToken) {
        return
      }
      workflowRunsApi
        .get(runToken)
        .then((fresh) => applyRun(fresh))
        .catch(() => undefined)
    })

    return () => {
      offFrame()
      offStatus()
      unsubscribeFromQuickStudyRun(samplingRunId)
    }
  }, [applyRun, notifySamplingActivity, runToken, samplingRunId, shouldPollSampling])

  const submitGate = useCallback(
    async (submission: WorkflowGateSubmission): Promise<void> => {
      const current = runRef.current
      if (!current) {
        return
      }

      const payload: SubmitWorkflowGatePayload = { decision: submission.decision }
      const overrides = buildGateOverrides(current, submission)
      if (overrides) {
        payload.overrides = overrides
      }
      if (submission.note) {
        payload.note = submission.note
      }

      try {
        applyRun(await workflowRunsApi.submitGate(current.token, payload))
      } catch (cause: unknown) {
        setError(toWorkflowRunError(cause, 'That decision did not go through.'))
      }
    },
    [applyRun],
  )

  /**
   * The chat revision. The reader typed a change while the gate was docked,
   * the intake turn answered `revision`, and the writer step now runs again
   * with those words and with the cards as they stand.
   *
   * The returned run is adopted like every other mutation's, which is what
   * takes the gate down: the step leaves `awaiting_approval` for the duration
   * of the redo and re-docks on its own when it lands.
   */
  const reviseStep = useCallback(
    async (revision: WorkflowGateRevision): Promise<boolean> => {
      const current = runRef.current
      if (!current) {
        return false
      }

      const payload: ReviseWorkflowStepPayload = {
        instruction: revision.instruction,
      }
      const override = buildRevisionOverride(current, revision)
      if (override) {
        payload.override = override
      }

      try {
        applyRun(await workflowRunsApi.revise(current.token, payload))
        return true
      } catch (cause: unknown) {
        setError(
          toWorkflowRunError(cause, 'That revision did not go through.'),
        )
        return false
      }
    },
    [applyRun],
  )

  const updateSegment = useCallback(
    async (segment: UpdateWorkflowRunSegmentPayload): Promise<boolean> => {
      const current = runRef.current
      if (!current) {
        return false
      }

      try {
        applyRun(await workflowRunsApi.updateSegment(current.token, segment))
        return true
      } catch {
        // The gate shows its own inline line; a run-level error would repeat it.
        return false
      }
    },
    [applyRun],
  )

  const retryStep = useCallback(
    async (stepId: string): Promise<void> => {
      const current = runRef.current
      if (!current) {
        return
      }

      // The endpoint takes `stepIndex`, never `stepId`. Compared against
      // `undefined` rather than truthiness, because step 0 is a real step.
      const stepIndex = current.steps.find((step) => step.stepId === stepId)
        ?.stepIndex

      try {
        applyRun(
          await workflowRunsApi.retry(
            current.token,
            stepIndex === undefined ? undefined : { stepIndex },
          ),
        )
      } catch (cause: unknown) {
        setError(toWorkflowRunError(cause, 'That retry did not go through.'))
      }
    },
    [applyRun],
  )

  /**
   * Cancel, including at a gate, where it IS the reject path: the server routes
   * a cancel on a run parked at a gate to `submitGate({ decision: 'reject' })`.
   * There is no reject endpoint and there is no reject button (D6).
   */
  const cancelRun = useCallback(async (): Promise<void> => {
    const current = runRef.current
    if (!current) {
      return
    }

    try {
      applyRun(await workflowRunsApi.cancel(current.token))
    } catch (cause: unknown) {
      setError(toWorkflowRunError(cause, 'That cancel did not go through.'))
    }
  }, [applyRun])

  return {
    run,
    template,
    isLoading,
    error,
    submitGate,
    reviseStep,
    updateSegment,
    retryStep,
    cancelRun,
    adoptRun: applyRun,
  }
}
